import { getDateLocale as getInterfaceDateLocale } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  MoreHorizontal,
  BarChart3,
  Copy,
  Download,
  ExternalLink,
  XCircle,
  RefreshCw,
  Trash2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { VideoWithShareStatus } from '@/types/share';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

interface ShareVideoCardCompactProps {
  video: VideoWithShareStatus;
  onCopyLink: () => void;
  onCreateLink: () => void;
  onRevoke: () => void;
  onRegenerate: () => void;
  onDelete: () => void;
}

// Status config simplificado: apenas Ativo e Desativado
const statusConfig = {
  active: { 
    get label() { return translateInterfaceText("Ativo"); },
    className: 'bg-[#2D6CF6] text-white' 
  },
  revoked: { 
    get label() { return translateInterfaceText("Desativado"); },
    className: 'bg-slate-500 text-white' 
  }
};

export function ShareVideoCardCompact({
  video,
  onCopyLink,
  onCreateLink,
  onRevoke,
  onRegenerate,
  onDelete
}: ShareVideoCardCompactProps) {
  useLanguage();
  const navigate = useNavigate();
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const linkStatus = video.shareLink?.status as 'active' | 'revoked';
  const isActive = linkStatus === 'active';

  const config = statusConfig[linkStatus] || statusConfig.active;

  const handleMetrics = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    navigate(`/distribuir/${video.id}`);
  };

  const handleCopyLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (video.shareLink) {
      const url = `${window.location.origin}/s/${video.shareLink.code}`;
      navigator.clipboard.writeText(url);
      toast.success(translateInterfaceText("Link copiado!"));
    }
  };

  const handleOpenLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (video.shareLink) {
      window.open(`${window.location.origin}/s/${video.shareLink.code}`, '_blank');
    }
  };

  const handleDownloadQR = (e: React.MouseEvent) => {
    e.stopPropagation();
    toast.success(translateInterfaceText("QR Code baixado!"));
    // Implementar download do QR
  };

  // Format metrics text for overlay
  const getMetricsText = () => {
    const copiesText = `${video.copiesCount} ${video.copiesCount === 1 ? 'cópia' : 'cópias'}`;
    
    if (video.lastCopyAt) {
      const lastCopy = formatDistanceToNow(new Date(video.lastCopyAt), { 
        addSuffix: false, 
        locale: getInterfaceDateLocale()
      });
      return `${copiesText} • Última: ${lastCopy}`;
    }
    
    return copiesText;
  };

  const metricsText = getMetricsText();

  return (
    <>
    <div
      className={cn(
        "group rounded-[14px] overflow-hidden bg-card border"
      )}
    >
      {/* Thumbnail container */}
      <div className="relative aspect-video bg-muted">
        <img 
          src={video.thumbUrl} 
          alt={video.title}
          className="w-full h-full object-cover"
        />
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        {/* Status Badge - Top Left */}
        <Badge className={cn(
          'absolute top-2.5 left-2.5 text-[10px] px-2 py-0.5 h-5 font-medium shadow-sm',
          config.className
        )}>
          {config.label}
        </Badge>
        
        {/* Menu "..." - Top Right */}
        <div className="absolute top-2 right-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-7 w-7 bg-black/40 hover:bg-black/60 backdrop-blur-sm"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-4 w-4 text-white" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 bg-popover z-50">
              {isActive ? (
                <>
                  <DropdownMenuItem onClick={handleCopyLink}>
                    <Copy className="h-4 w-4 mr-2" />
                    {translateInterfaceText("Copiar link")}</DropdownMenuItem>
                  <DropdownMenuItem onClick={handleDownloadQR}>
                    <Download className="h-4 w-4 mr-2" />
                    {translateInterfaceText("Baixar QR Code")}</DropdownMenuItem>
                  <DropdownMenuItem onClick={handleOpenLink}>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    {translateInterfaceText("Abrir link")}</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => { e.stopPropagation(); setRevokeDialogOpen(true); }}
                    className="text-destructive data-[highlighted]:bg-red-600 data-[highlighted]:text-white"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    {translateInterfaceText("Desativar")}</DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onRegenerate(); }}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {translateInterfaceText("Reativar")}</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => { e.stopPropagation(); setDeleteDialogOpen(true); }}
                    className="text-destructive data-[highlighted]:bg-red-600 data-[highlighted]:text-white"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {translateInterfaceText("Excluir")}</DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {/* Bottom content (metrics + title) */}
        <div className="absolute bottom-0 left-0 right-0 p-3">
          {metricsText && (
            <span className="text-[11px] text-white/80 font-medium block mb-1">
              {metricsText}
            </span>
          )}
          <h3 className="text-white font-medium text-sm line-clamp-2 leading-tight">
            {video.title}
          </h3>
        </div>
      </div>
      
      {/* Footer with Métricas button */}
      <div className="p-3 pt-2">
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full h-8 text-xs"
          onClick={handleMetrics}
        >
          <BarChart3 className="h-3.5 w-3.5 mr-1.5" />
          {translateInterfaceText("Métricas")}</Button>
      </div>
    </div>

      {/* Modais de confirmação */}
      {/* Modal de confirmação — Desativar */}
      <AlertDialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <AlertDialogContent className="!w-[40vh] md:w-[35vh] rounded-xl p-6">
          <AlertDialogHeader>
            <AlertDialogTitle>{translateInterfaceText("Desativar compartilhamento")}</AlertDialogTitle>
            <AlertDialogDescription>
              {translateInterfaceText("Tem certeza que deseja desativar o link de compartilhamento? O link deixará de funcionar imediatamente.")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-11 px-5 rounded-[10px] border-border text-muted-foreground hover:bg-muted hover:text-[#64748b] transition-colors">
              {translateInterfaceText("Cancelar")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setRevokeDialogOpen(false); onRevoke(); }}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              {translateInterfaceText("Desativar")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de confirmação — Excluir */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="!w-[40vh] md:w-[35vh] rounded-xl p-6">
          <AlertDialogHeader>
            <AlertDialogTitle>{translateInterfaceText("Excluir compartilhamento")}</AlertDialogTitle>
            <AlertDialogDescription>
              {translateInterfaceText("Tem certeza que deseja excluir este compartilhamento? Esta ação não pode ser desfeita.")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-11 px-5 rounded-[10px] border-border text-muted-foreground hover:bg-muted hover:text-[#64748b] transition-colors">
              {translateInterfaceText("Cancelar")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setDeleteDialogOpen(false); onDelete(); }}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              {translateInterfaceText("Excluir")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function ShareVideoCardCompactSkeleton() {
  useLanguage();
  return (
    <div className="rounded-[14px] overflow-hidden">
      <Skeleton className="w-full aspect-video" />
    </div>
  );
}
